import { AfterViewInit, ChangeDetectorRef, Component, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@env/environment';
import {
  CommonIP,
  CommonIPLite,
  CommonIPStats,
  CommonIPTrifoldUploadResponse,
  DialogMessageEvent,
  DocumentSubType,
  DST_PROP_BY_IP_TYPE,
  FileData,
  FileWithProgress,
  IP,
  IpTypeLabel,
  RefFileResponse,
  RelatedFileGroup,
  SubTypeByIpType,
  TrifoldDetailMap,
  UploadStats,
  UploadSubTypeGroupingMode,
} from '@model/ftf-api.model';
import { Store } from '@ngrx/store';
import { AccountService } from '@service/account.service';
import { AddinContextService } from '@service/addin-context.service';
import { ApiService, emptyCriteria } from '@service/api.service';
import { AuthService } from '@service/auth.service';
import { LoaderService } from '@service/loader.service';
import { LoggerService } from '@service/logger.service';
import { SettingsService } from '@service/settings.service';
import { selectDocumentSubTypesState } from '@store/selectors/document-subtypes.selectors';
import { base64ToBlob, base64ToFile, FileWithProgressHelper } from '@utils/file-utils';
import { FileBasedMailItem } from '@utils/office-utils';
import { SelectionFormArray } from '@utils/selection-form-array';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  delay,
  filter,
  finalize,
  from,
  fromEvent,
  map,
  merge,
  mergeMap,
  NEVER,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  toArray,
} from 'rxjs';

import { TableEvent, TrifoldItemEvent, UploadTableFormControl, UploadTableFormData } from './upload-table/upload-table.model';
import { UploaderPresentationComponent } from './uploader-presentation.component';

export interface FileDataCache {
  id: string;
  data: Blob | null;
}

export type UploadResponse =
  | RefFileResponse
  | {
      uploadFileResp: CommonIPTrifoldUploadResponse;
      uploadKeywordResp: CommonIPTrifoldUploadResponse | null;
      uploadDocNoteResp: CommonIPTrifoldUploadResponse | null;
    };
@Component({
  selector: 'app-uploader-container',
  imports: [UploaderPresentationComponent],
  template: `
    <app-uploader-presentation
      #presentation
      [form]="form"
      [uploadTableForms]="uploadTableForms"
      [searchItems$]="searchItems$"
      [paginationInfo$]="paginationInfo$"
      [dependantDataLoaded]="dependantDataLoaded"
      [uploadInitiated]="uploadInitiated"
      [uploadedFilesProgress]="uploadedFilesProgress"
      [skippedFilesProgress]="skippedFilesProgress"
      [allSubTypes]="allSubTypes"
      [trifoldMap]="trifoldMap"
      [ipTypes]="ipTypes"
      [relFileGroups]="relFileGroups"
      [knownIpItems]="knownIpItems"
      [placeholderText]="placeholderText"
      [hasUploadPermission]="hasUploadPermission.bind(this)"
      [progressPercentage]="getProgressPercentage()"
      (addToClick)="onAddToClick($event)"
      (searchSelectionChange)="onSearchSelectionChange($event)"
      (checkboxChange)="onCheckboxChange($event)"
      (closeTable)="onCloseTable($event)"
      (openFTF)="onOpenInFTF($event)"
      (openIPMS)="onOpenInIPMS($event)"
      (pageChange)="onPageChange($event)"
      (browseFiles)="onBrowseFiles($event)"
      (uploadFiles)="uploadFiles()"
    ></app-uploader-presentation>
  `,
})
export class UploaderContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  private apiService = inject(ApiService);
  private authService = inject(AuthService);
  private addinConfig = inject(AddinContextService);
  private accountService = inject(AccountService);
  private settingsService = inject(SettingsService);
  private loaderService = inject(LoaderService);
  private loggerService = inject(LoggerService);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);

  // Message queue for processing messages in order
  private messageQueue = new Subject<DialogMessageEvent>();
  protected $ngDestroyed = new Subject<void>();
  private currentPageIndex = new BehaviorSubject<number>(0);
  public paginationInfo = new BehaviorSubject<{ currentPage: number; totalPages: number }>({ currentPage: 1, totalPages: 1 });
  public paginationInfo$ = this.paginationInfo.asObservable();
  private firstFormMap: { [key: string]: FormGroup } = {};
  private defaultTrifold: IP.TrifoldEnum = IP.TrifoldEnum.L;
  public uploadedFilesProgress: string = '';
  public skippedFilesProgress: string = '';
  private uploadedFilesCount: number = 0;
  private skippedFilesCount: number = 0;
  private totalFilesCount: number = 0;
  public placeholderText: string = 'Search for cases';

  dependantDataLoaded: boolean;
  uploadInitiated: boolean;
  knownIpItems: { identifier: string; ipType: IP.TypeEnum }[];
  relFileGroups: RelatedFileGroup[];
  trifoldMap: TrifoldDetailMap;
  ipTypes: IpTypeLabel<IP.TypeEnum>[];
  fileDataCache: { [key: string]: FileDataCache };
  allSubTypes: SubTypeByIpType;

  uploadTableForms: FormArray<FormGroup<UploadTableFormControl>>;

  searchItems$: Observable<CommonIP[]>;
  disableUpload$: Observable<boolean>;

  form: FormGroup;

  @ViewChild(UploaderPresentationComponent) presentationComponent!: UploaderPresentationComponent;

  constructor() {
    this.authService.canCallApi$.pipe(takeUntil(this.$ngDestroyed)).subscribe((canCallApi) => {
      if (canCallApi) return;
      // if user loses session, no point in keeping dialog open
      this.messageParent({ type: 'closeDialog' });
    });

    this.dependantDataLoaded = false;
    this.uploadInitiated = false;
    this.relFileGroups = [];
    this.fileDataCache = {};
    this.knownIpItems = [];

    // default labels for trifolders
    this.trifoldMap = this.accountService.trifoldMap;
    this.ipTypes = this.accountService.ipTypes;
    this.allSubTypes = {
      Matter: {},
      Patent: {},
      Trademark: {},
      Reference: {},
      CaseReference: {},
    };
    this.uploadTableForms = this.fb.array<FormGroup<UploadTableFormControl>>([], {
      validators: [this.validateAtLeastOneSelection.bind(this)],
    });
    this.form = this.fb.group({
      search: [{ value: null, disabled: true }],
      ipType: [{ value: null, disabled: true }],
    });
    this.searchItems$ = this.createSearchItemsObservable();
    this.disableUpload$ = this.uploadTableForms.statusChanges
      .pipe(
        startWith(this.uploadTableForms.invalid),
        map(() => this.uploadTableForms.invalid)
      )
      .pipe(takeUntil(this.$ngDestroyed));

    // bucketise our subtypes based on ip type and doc type
    this.store
      .select(selectDocumentSubTypesState)
      .pipe(takeUntil(this.$ngDestroyed))
      .subscribe((state) => {
        if (!state.isLoaded) return;
        this.bucketizeSubTypes(state.subtypes);
        this.dependantDataLoaded = true;
      });
  }

  /**
   * Buckets the given subTypes based on IP type and document type, respecting user settings.
   *
   * @param {DocumentSubType[]} subTypes - The array of subTypes to be bucketized.
   * @returns {void}
   */
  bucketizeSubTypes(subTypes: DocumentSubType[]): void {
    // Initialize subtypes structure for each IP type
    const ipTypes = [IP.TrifTypeEnum.Matter, IP.TrifTypeEnum.Patent, IP.TrifTypeEnum.Trademark];
    ipTypes.forEach((ipType) => {
      this.allSubTypes[ipType] = {};
    });

    // Get grouping mode from settings
    const settings = this.settingsService.getSettings();
    const groupingMode = settings.uploadSubTypeGroupingMode;
    const useDocTypeGrouping = groupingMode === UploadSubTypeGroupingMode.DocType;

    if (useDocTypeGrouping) {
      // Original bucketizing by document type
      subTypes.forEach((type) => {
        const docType = type.doctypeName!;
        ipTypes.forEach((ipType) => {
          const include = type[DST_PROP_BY_IP_TYPE[ipType]] ?? false;
          if (!include) return;
          if (!this.allSubTypes[ipType][docType]) {
            this.allSubTypes[ipType][docType] = [];
          }
          this.allSubTypes[ipType][docType].push({ ...type });
        });
      });
    } else {
      ipTypes.forEach((ipType) => {
        this.allSubTypes[ipType]['All Sub-Types'] = [];

        // Filter and sort applicable subtypes for this IP type
        // Add all filtered types to the "All" category
        this.allSubTypes[ipType]['All Sub-Types'] = subTypes
          .filter((type) => type[DST_PROP_BY_IP_TYPE[ipType]] ?? false)
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
          .map((type) => ({ ...type }));
      });
    }
  }

  ngOnInit(): void {
    // Initialize the message queue
    this.initializeMessageQueue();
    this.form.get('ipType')?.valueChanges.subscribe(() => {
      this.cdr.detectChanges();
    });
    // Handle route parameters
    this.route.queryParamMap.subscribe((params) => {
      const identifier = params.get('identifier') ?? '';
      const ipType = params.get('ipType') ?? '';
      this.defaultTrifold = (params.get('trifold') ?? 'L') as IP.TrifoldEnum;
      if (ipType === 'Reference' || identifier) {
        // when dialog first loads, if an identifier was provided we need to fetch details & files for it
        this.addUploadTables(identifier, ipType as IP.TypeEnum).subscribe();
      }
    });
    // load users preferences and permissions, etc..
    this.accountService.isAccountDetailsLoaded$.pipe(takeUntil(this.$ngDestroyed)).subscribe((loaded) => {
      if (!loaded) return;

      // load users preferences and permissions, etc..
      this.trifoldMap = this.accountService.trifoldMap;
      // Enable only the IP Types that the user has access to
      this.ipTypes = this.accountService.ipTypes;
      if (this.ipTypes.length > 0) {
        this.form.enable();
      }
    });
  }

  /**
   * Initializes the message queue processing system
   */
  private initializeMessageQueue(): void {
    this.messageQueue
      .pipe(
        // concatMap ensures messages are processed one at a time, in order
        concatMap((message: DialogMessageEvent) => {
          if (message.type === 'fileGroup') {
            const fg = message.data as RelatedFileGroup;
            // date get converted to string when serialized
            fg.files.forEach((file) => (file.date = new Date(file.date)));
            this.relFileGroups.push(fg);
            return this.addMissingUploadTables().pipe(
              catchError((error) => {
                this.loggerService.error('[UploaderContainerComponent] Error adding missing upload tables:', error);
                return of(null);
              })
            );
          } else if (/^fileData/.test(message.type)) {
            // link up data with each file entry
            const rec = message.data as FileData;

            if (message.type === 'fileData') {
              // if its not a data error then we cache the data
              this.fileDataCache[rec.id] = { id: rec.id, data: base64ToBlob(rec.data) };
            }

            return new Observable<null>((observer) =>
              this.ngZone.run(() => {
                const error = message.type === 'fileDataError';
                // In off chance form isn't created yet set the dataFile to true
                this.relFileGroups.forEach((group) => {
                  group.files
                    .filter((file) => file.id === rec.id)
                    .forEach((file) => {
                      file.dataFile = !error;
                    });
                });
                this.uploadTableForms.controls.forEach((control) => {
                  const files = control.get('files') as SelectionFormArray<FormGroup>;
                  files.controls.forEach((file) => {
                    if (file.get('id')?.value === rec.id) {
                      file.patchValue({ dataFileError: error, dataFile: !error });
                      file.markAsTouched();
                    }
                  });
                });
                observer.next(null);
                observer.complete();
              })
            );
          }

          return of(null);
        }),
        takeUntil(this.$ngDestroyed)
      )
      .subscribe({
        error: (error) => {
          this.loggerService.error('[UploaderContainerComponent] Error processing message queue:', error);
          // Even on error, the subscription continues because concatMap handles errors at the inner observable level
        },
      });
  }

  ngAfterViewInit(): void {
    // setup listener for messages from the parent window (the taskpane)
    if (this.addinConfig.isOfficeActive) {
      Office.context.ui.addHandlerAsync(
        Office.EventType.DialogParentMessageReceived,
        (arg) => {
          if ('message' in arg && /^{.*?"type"\s*:.*}/s.test(arg.message)) {
            this.onMessageReceived(JSON.parse(arg.message) as DialogMessageEvent);
          }
        },
        () => {
          // only send this message when the handler is fully registered
          this.messageParent({ type: 'dialogLoaded' });
        }
      );
    } else {
      // for when this is ran outside of an office product
      fromEvent<MessageEvent>(window, 'message')
        .pipe(takeUntil(this.$ngDestroyed))
        .subscribe((event: MessageEvent) => {
          if (window !== event.source && event.origin === location.origin) {
            if (!/^{.*?"type"\s*:.*}/s.test(event.data)) return;
            this.onMessageReceived(JSON.parse(event.data) as DialogMessageEvent);
          }
        });
      this.messageParent({ type: 'dialogLoaded' });
    }
  }

  /**
   * Sends a message to the parent window or Office add-in.
   * If the add-in is active, the message is sent using Office.context.ui.messageParent.
   * Otherwise, the message is sent using window.parent.postMessage.
   * @param message - The message to send. It can be a string or an object.
   */
  messageParent(message: unknown | string) {
    const messageToSend = typeof message === 'string' ? message : JSON.stringify(message);
    if (this.addinConfig.isOfficeActive) {
      Office.context.ui.messageParent(messageToSend);
    } else {
      window.parent.postMessage(messageToSend, location.origin);
    }
  }

  /**
   * Checks if the user has permission to upload files of the specified IP type.
   * @param ipType The IP type to check.
   * @returns True if the user has upload permission, false otherwise.
   */
  hasUploadPermission(ipType: IP.TypeEnum): boolean {
    if (ipType === 'Reference' || ipType === 'CaseReference') {
      return this.accountService.hasRole('ROLE_REFERENCE_CREATE');
    } else {
      return this.accountService.uploadableTrifolds[ipType as IP.TrifTypeEnum].length > 0;
    }
  }

  clearFirstFormMapForNewIpType(newIpType: IP.TypeEnum): void {
    // Instead of clearing the entire map, only clear forms for different IP types
    const keysToRemove: string[] = [];

    // Check each form in the map
    Object.keys(this.firstFormMap).forEach((key) => {
      const form = this.firstFormMap[key];
      const formIpType = form.get('ipType')?.value;

      // If the form's IP type is different from the new IP type, mark it for removal
      if (formIpType && formIpType !== newIpType) {
        keysToRemove.push(key);
      }
    });

    // Remove forms for different IP types
    keysToRemove.forEach((key) => {
      delete this.firstFormMap[key];
    });
  }

  /**
   * Clears the first form map for the specified IP type.
   * @param ipType The new IP type to set.
   */
  onAddToClick(ipType: IP.TypeEnum): void {
    if (ipType === 'Reference') {
      this.clearFirstFormMapForNewIpType(ipType);
      this.addUploadTables('', ipType).subscribe();
    } else {
      this.clearFirstFormMapForNewIpType(ipType);
      this.ngZone.run(() => {
        this.form.patchValue({ ipType });
        this.updatePlaceholderText(ipType);
      });
    }
  }

  /**
   * Receives a message from the parent window and adds it to the message queue
   * @param message The message received from the parent window
   */
  onMessageReceived(message: DialogMessageEvent) {
    if (!environment.production) {
      this.loggerService.debug('[UploaderContainerComponent] Message received:', message);
    }
    // Add the message to the queue
    this.messageQueue.next(message);
  }

  /**
   * Handles the file browsing event.
   * @param event The mouse event.
   * @returns
   */
  onBrowseFiles(event: Event) {
    // Office doesn't let us use showOpenFilePicker({ multiple: true }) in a dialog
    const target = event.target as HTMLInputElement;
    if (!target.files) return;
    const files = Array.from(target.files);

    this.loaderService.show();

    from(files)
      .pipe(
        // Process each file based on whether it's a mail file or not
        mergeMap((file) => {
          if (/\.(msg|eml)$/i.test(file.name)) {
            // Process mail files and catch errors
            return FileBasedMailItem.fromFile(file, this.loggerService).pipe(
              mergeMap((item) => {
                const emailData = FileWithProgressHelper.fromMailItem(item, this.defaultTrifold);
                const fwp = [emailData];
                item.attachments.forEach((att) => {
                  const attData = FileWithProgressHelper.fromMailAttachment(item, this.defaultTrifold, att);
                  if (attData) fwp.push(attData);
                });
                return item
                  .fetchAllContent((resp) => {
                    const id = resp.detail.type === 'att' ? `att-${resp.detail.id}` : resp.detail.id;
                    const fileRec = fwp.find((file) => file.id === id);
                    if (!fileRec) {
                      // bail out if we can't find the target file
                      throw new Error('Target file not found');
                    }
                    if (!resp.success) {
                      // bail out if there is failure
                      throw resp.error;
                    }
                    // build file group
                    const data = resp.value!;
                    fileRec.dataFile =
                      data.format === 'base64' ? base64ToFile(data.content, fileRec.name) : new File([data.content], fileRec.name);
                  }, NEVER)
                  .pipe(
                    tap(() => {
                      this.ngZone.run(() => {
                        fwp.forEach((file) => {
                          this.fileDataCache[file.id] = { id: file.id, data: file.dataFile as File };
                          file.dataFile = true;
                        });
                        const data: RelatedFileGroup = { id: emailData.id, display: emailData.title, files: fwp };
                        this.loggerService.debug('[UploaderContainerComponent] Mail file processed:', data);
                        // don't need 2 copies floating around
                        this.relFileGroups.push(data);
                        this.addMissingUploadTables().subscribe();
                      });
                    })
                  );
              }),
              // If mail processing fails, return the original file to be treated as non-mail
              catchError(() => of(file))
            );
          } else {
            // Non-mail files are passed through directly
            return of(file);
          }
        }, 3),
        // Filter out nulls and collect only the non-mail files plus failed mail files
        filter((result): result is File => result instanceof File),
        toArray(),
        // Send all non-mail and failed mail files to the dialog
        tap((otherFiles) => {
          this.addManualFiles(otherFiles);
        }),
        finalize(() => this.loaderService.hide())
      )
      .subscribe();
  }

  /**
   * Handles the checkbox change event.
   * @param event - The checkbox change event.
   * @param item - The CommonIP item associated with the checkbox.
   */
  onCheckboxChange(data: { event: MatCheckboxChange; item: CommonIP }): void {
    const { event, item } = data;
    const ipType = item.ipType as IP.TypeEnum;

    if (event.checked) {
      this.knownIpItems.push({ identifier: item.identifier, ipType });
      this.addUploadTables(item.identifier, ipType).subscribe();
    } else {
      const selectedIndex = this.knownIpItems.findIndex(
        (selected) => selected.identifier === item.identifier && selected.ipType === ipType
      );
      if (selectedIndex !== -1) {
        this.knownIpItems.splice(selectedIndex, 1);
      }
      const tablePrefix = `${item.identifier}||${ipType}`;

      for (let i = this.uploadTableForms.controls.length - 1; i >= 0; i--) {
        const control = this.uploadTableForms.controls[i];
        const formId = control.get('id')?.value as string;

        if (formId.startsWith(tablePrefix)) {
          this.uploadTableForms.removeAt(i);
        }
      }
      const itemIndex = this.knownIpItems.findIndex((knownItem) => knownItem.identifier === item.identifier && knownItem.ipType === ipType);

      if (itemIndex !== -1) {
        this.knownIpItems.splice(itemIndex, 1);
      }
    }
  }

  /**
   * Handles the search selection change event.
   * @param $event The autocomplete selected event.
   */
  onSearchSelectionChange($event: MatAutocompleteSelectedEvent): void {
    const item = $event.option.value as CommonIP;
    let ipType = item.ipType as IP.TypeEnum;

    if (ipType === 'Patent' && this.form.get('ipType')?.value === 'CaseReference') {
      ipType = 'CaseReference';
    }

    // Clear the firstFormMap for the new IP type
    this.clearFirstFormMapForNewIpType(ipType);

    // Add the table
    this.addUploadTables(item.identifier, ipType).subscribe();

    // Get rid of selection / value
    this.ngZone.run(() => {
      this.form.patchValue({ search: null, ipType: null });
      $event.option.deselect();
    });

    this.resetAutocomplete();
  }

  /**
   * Handles the event when a upload table is closed.
   *
   * @param event - The event object containing details about the table close action.
   */
  onCloseTable(event: TableEvent) {
    const identifier = event.form.get('identifier')?.value;
    const ipType = event.form.get('ipType')?.value;

    if (identifier && ipType) {
      const selectedIndex = this.knownIpItems.findIndex((selected) => selected.identifier === identifier && selected.ipType === ipType);
      if (selectedIndex !== -1) {
        this.knownIpItems.splice(selectedIndex, 1);
      }
    }
    // remove table from forms
    const index = this.uploadTableForms.controls.findIndex((control) => control === event.form);
    if (index > -1) {
      this.uploadTableForms.removeAt(index);
    }
  }

  /**
   * Opens the specified URL in FTF.
   * @param event - The trifold event that triggered the method.
   */
  onOpenInFTF(event: TrifoldItemEvent): void {
    const type = event.item.ipType === IP.TypeEnum.Patent ? 'Case' : event.item.ipType;
    const url = `${this.settingsService.getParsedBaseUrl()}index.jsp?type=${type}&redirectCase=${encodeURIComponent(event.item.identifier)}`;
    window.open(url, '_blank');
  }

  /**
   * Opens the specified URL in IPMS.
   * @param event - The trifold event that triggered the method.
   */
  onOpenInIPMS(event: TrifoldItemEvent): void {
    if (event.item.caseUrl) {
      window.open(event.item.caseUrl, '_blank');
    }
  }

  /**
   * Handles page changes in the paginated autocomplete
   * @param pageIndex The new page index (zero-based)
   */
  onPageChange(pageIndex: number): void {
    this.ngZone.run(() => {
      this.currentPageIndex.next(pageIndex);
      // Force Angular to detect changes and update the view
      this.cdr.detectChanges();
    });
  }

  /**
   * Adds manual files to the uploader.
   * @param files The array of files to add.
   * @returns
   */
  addManualFiles(files: File[]) {
    if (files.length === 0) return;

    this.ngZone.run(() => {
      const fileRecs = files.map((file) => FileWithProgressHelper.fromFile(file, this.defaultTrifold, true));
      // cache data
      fileRecs.forEach((file) => {
        if (file.dataFile instanceof File) {
          this.fileDataCache[file.id] = { id: file.id, data: file.dataFile };
          // don't need 2 copies floating around
          file.dataFile = true;
        }
      });

      // see if we already have a manual files group
      const manualGroup = this.relFileGroups.find((group) => group.id === 'manual');
      if (manualGroup) {
        manualGroup.files.push(...fileRecs);
        // add to the "Local Files" forms that already exist
        this.presentationComponent?.uploadTables?.forEach((table) => {
          if (table.relFileGroupId === 'manual') {
            table.addFiles(fileRecs);
          }
        });
      } else {
        const data: RelatedFileGroup = {
          id: 'manual',
          display: 'Local files',
          files: fileRecs,
        };
        this.relFileGroups.push(data);
        this.addMissingUploadTables().subscribe();
      }
    });
  }

  /**
   * Adds any missing upload tables
   * @returns Observable that completes when all tables are added
   */
  addMissingUploadTables(): Observable<void> {
    const addTableObservables: Observable<void>[] = [];

    this.knownIpItems.forEach((item) => {
      addTableObservables.push(this.addUploadTables(item.identifier, item.ipType as IP.TypeEnum));
    });

    // If no tables to add, return completed observable
    if (addTableObservables.length === 0) {
      return of();
    }

    // Combine all observables and complete when all are done
    return merge(...addTableObservables).pipe(
      // Use toArray to wait for all inner observables to complete
      toArray(),
      // Map the array result to void
      map(() => {})
    );
  }

  /**
   * Adds upload tables for the specified identifier and IP type
   * @param identifier The identifier for the IP item
   * @param ipType The type of IP
   * @returns Observable that completes when all tables are added
   */
  addUploadTables(identifier: string, ipType: IP.TypeEnum): Observable<void> {
    // add to know ip items if doesn't already exist
    if (!this.knownIpItems.some((item) => item.identifier === identifier && item.ipType === ipType)) {
      this.knownIpItems.push({ identifier, ipType });
    }

    // Create observables array to track all table additions
    const tableObservables: Observable<void>[] = [];

    // now build the upload tables
    this.relFileGroups.forEach((group) => {
      if (ipType !== IP.TypeEnum.Reference) {
        // Check if there's already a form for this group
        const existingForm = this.firstFormMap[group.id];
        if (existingForm) {
          // Get the ipType from the first form for this group
          const existingIpType = existingForm.get('ipType')?.value;

          // Skip adding a new table if the ipType doesn't match
          // This prevents adding the same files to different IP types
          if (existingIpType && existingIpType !== ipType) {
            tableObservables.push(this.addUploadTable(identifier, ipType, group.id));
            return;
          }

          // Optional: Add files from this group to the form if needed
          const filesArray = existingForm.get('files') as SelectionFormArray<FormGroup>;
          tableObservables.push(this.addUploadTable(identifier, ipType, group.id, filesArray));
          return;
        }
      }

      tableObservables.push(this.addUploadTable(identifier, ipType, group.id));
    });

    // If no tables to add, return completed observable
    if (tableObservables.length === 0) {
      return of();
    }

    // Combine all observables and complete when all are done
    return merge(...tableObservables).pipe(
      toArray(),
      map(() => {})
    );
  }

  /**
   * Adds an upload table for the specified identifier, IP type, and file group
   * @param identifier The identifier for the IP item
   * @param ipType The type of IP
   * @param relFileGroupId The ID of the related file group
   * @param existingFilesArray Optional array of existing files to include
   * @returns Observable that completes when the table is added
   */
  addUploadTable(
    identifier: string,
    ipType: IP.TypeEnum,
    relFileGroupId: string,
    existingFilesArray?: SelectionFormArray<FormGroup>
  ): Observable<void> {
    if (!ipType) return of();

    const id = `${identifier}||${ipType}||${relFileGroupId}`;
    const groupKey = `${relFileGroupId}`;

    // check if it already exists in forms
    const existingForm = this.uploadTableForms.controls.find((control) => {
      return control.get('id')?.value === id;
    });
    if (existingForm) return of();

    const validators = ipType !== 'Reference' ? [Validators.required] : [];

    // Create a new files array
    const filesArray = new SelectionFormArray<FormGroup>([]);

    // If we have an existing files array, create new controls with copied values
    if (existingFilesArray && existingFilesArray.controls.length > 0) {
      // Create new controls for each file in the existing array
      existingFilesArray.controls.forEach((control) => {
        // Get the original values
        const originalValues = control.getRawValue();

        /*
         * Please keep parity with the UploadTableComponent.FormBuilder `createFileFormGroup`
         */
        const newFileGroup = this.fb.group({
          id: this.fb.control(originalValues.id, { nonNullable: true }),
          fileNameTruncated: this.fb.control(originalValues.fileNameTruncated, { nonNullable: true }),
          titleTruncated: this.fb.control(originalValues.titleTruncated, { nonNullable: true }),
          name: this.fb.control(originalValues.name, { nonNullable: true }),
          originalFilename: this.fb.control(originalValues.originalFilename, { nonNullable: true }),
          originalTitle: this.fb.control(originalValues.originalTitle, { nonNullable: true }),
          title: this.fb.control(originalValues.title, { nonNullable: true }),
          hasAttachments: this.fb.control(originalValues.hasAttachments, { nonNullable: true }),
          size: this.fb.control(originalValues.size, { nonNullable: true }),
          date: this.fb.control(originalValues.date, { nonNullable: true }),
          progress: this.fb.control(originalValues.progress, { nonNullable: true }),
          trifold: this.fb.control(originalValues.trifold, { nonNullable: true }),
          urgent: this.fb.control(originalValues.urgent, { nonNullable: true }),
          subType: this.fb.control(originalValues.subType),
          keywords: this.fb.control(originalValues.keywords),
          docNotes: this.fb.control(originalValues.docNotes),
          dataFile: this.fb.control(originalValues.dataFile),
          isMainEmail: this.fb.control(originalValues.isMainEmail),
          isInline: this.fb.nonNullable.control(originalValues.isInline === true),
          exportControl: this.fb.control(originalValues.exportControl),
        });

        if (existingFilesArray.selection.isSelected(control)) {
          filesArray.selection.select(newFileGroup);
        }
        // Add the new control to the array
        filesArray.controls.push(newFileGroup);
      });
    }

    const tableForm = new FormGroup<UploadTableFormControl>({
      id: this.fb.control<string>(id, { validators: [Validators.required], nonNullable: true }),
      identifier: this.fb.control<string>(identifier ?? '', { validators, nonNullable: true }),
      ipType: this.fb.control<IP.TypeEnum>(ipType, { validators: [Validators.required], nonNullable: true }),
      files: filesArray,
      includeFiles: this.fb.control(false, { nonNullable: true }),
    });

    if (existingFilesArray) {
      tableForm.patchValue({ includeFiles: true });
    }

    // Store the form for this group
    this.firstFormMap[groupKey] = tableForm;

    // Create a subject that will emit when the table has been added
    return new Observable<void>((observer) => {
      Promise.resolve().then(() => {
        this.ngZone.run(() => {
          const subscription = this.presentationComponent.uploadTables.changes.pipe(take(1), delay(300)).subscribe(() => {
            // Complete the observable to signal that the table has been added
            this.loggerService.debug('[UploaderContainerComponent] Table added');
            observer.next();
            observer.complete();
            subscription.unsubscribe();
          });
          this.loggerService.debug('[UploaderContainerComponent] Inserting table');
          // Insert the tableForm at the beginning of the uploadTableForms array
          this.uploadTableForms.insert(0, tableForm);
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }

  /**
   * Validates that at least one of the upload tables has a file selected.
   * @returns
   */
  validateAtLeastOneSelection(control: AbstractControl): ValidationErrors | null {
    const someSelected = (control as FormArray).controls.some((group) => {
      const sfa = group.get('files') as SelectionFormArray<FormGroup>;
      return sfa.selection.selected.length > 0;
    });
    return someSelected ? null : { noSelections: true };
  }

  /**
   * Uploads the files to the server.
   */
  uploadFiles() {
    this.uploadInitiated = true;

    const itemStats = new Map<string, CommonIPStats>();
    const emailStats = new Map<string, { success: number; failure: number }>();

    this.loaderService.hide(true);
    const groupedData = this.uploadTableForms.getRawValue() as UploadTableFormData[];
    this.totalFilesCount = groupedData.reduce((acc, group) => acc + group.files.length, 0);

    from(groupedData)
      .pipe(
        map((group) => {
          // the group id is the combination fo several things.. we particularly want to get the emailId for stat calculations
          const [identifier, ipType, emailId] = group.id.split('||');
          // initialize the stats on the item
          const itemStatsKey = `${identifier}||${ipType}`;
          if (!itemStats.has(itemStatsKey)) {
            itemStats.set(itemStatsKey, { item: { identifier, ipType: group.ipType }, successFiles: [], failureFiles: [] });
          }
          // initialize the stats on the email
          if (emailId && !emailStats.has(emailId)) {
            emailStats.set(emailId, { success: 0, failure: 0 });
          }
          return { ...group, itemStatsKey, emailId };
        }),
        mergeMap((group) => {
          return from(group.files).pipe(
            // Upload files in parallel, but limit to 2 at a time
            mergeMap(
              (file) =>
                this.uploadFile(group.identifier ?? '', group.ipType, file).pipe(
                  tap(() => {
                    itemStats.get(group.itemStatsKey)!.successFiles.push(file.name);
                    emailStats.get(group.emailId)!.success++;
                    this.uploadedFilesCount++;
                    this.updateFileUploadProgress();
                  }),
                  catchError((error) => {
                    itemStats.get(group.itemStatsKey)!.failureFiles.push(file.name);
                    emailStats.get(group.emailId)!.failure++;
                    this.loggerService.error('[UploaderContainerComponent] Upload error:', error);
                    this.skippedFilesCount++;
                    this.updateFileUploadProgress();
                    // Return an empty observable to continue with the next file
                    return of(null);
                  })
                ),
              2
            )
          );
        }),
        // Collect results into an array
        toArray()
      )
      .subscribe({
        complete: () => {
          this.uploadedFilesProgress = '';
          this.skippedFilesProgress = '';
          this.uploadInitiated = false;
        },
        next: () => {
          this.loggerService.debug('[UploaderContainerComponent] Files uploaded');
          // clear out the file cache
          this.fileDataCache = {};
          // finish formatting stats
          const stats: UploadStats = { items: [], emails: {} };
          itemStats.forEach((value) => {
            // dedup the file names
            value.successFiles = [...new Set(value.successFiles)].sort();
            value.failureFiles = [...new Set(value.failureFiles)].sort();
            stats.items.push(value);
          });
          emailStats.forEach((value, key) => {
            stats.emails[key] = value;
          });
          // communicate back to the parent
          this.refreshTrifolds(stats);
        },
        error: (error) => {
          this.loggerService.error('[UploaderContainerComponent] Upload error:', error);
        },
      });
  }

  /**
   * Refreshes the trifold view with the latest upload statistics.
   * @param stats The upload statistics to use for refreshing the view.
   */
  refreshTrifolds(stats: UploadStats) {
    // Refresh the trifold view
    const refreshItems: CommonIPLite[] = this.uploadTableForms.controls
      .map((control) => ({ identifier: control.get('identifier')?.value, ipType: control.get('ipType')?.value }))
      // filter off references
      .filter((item) => item.identifier && item.ipType && item.ipType !== IP.TypeEnum.Reference)
      // ensure it is our 3 trifold types
      .map((item) => ({
        identifier: item.identifier!,
        ipType: (item.ipType === 'CaseReference' ? 'Patent' : item.ipType) as IP.TrifTypeEnum,
      }))
      // dedup
      .reduce<CommonIPLite[]>((acc, item) => {
        const key = `${item.identifier}-${item.ipType}`;
        if (!acc.some((existingItem) => `${existingItem.identifier}-${existingItem.ipType}` === key)) {
          acc.push(item);
        }
        return acc;
      }, []);

    this.messageParent({ type: 'refreshTrifolds', data: { refreshItems, stats } });
  }

  /**
   * Gets the progress percentage of the file uploads.
   * @returns The progress percentage as a number.
   */
  getProgressPercentage(): number {
    return ((this.uploadedFilesCount + this.skippedFilesCount) / this.totalFilesCount) * 100;
  }

  /**
   * Updates the file upload progress strings.
   */
  private updateFileUploadProgress(): void {
    this.uploadedFilesProgress = this.uploadedFilesCount > 0 ? `${this.uploadedFilesCount} / ${this.totalFilesCount} files uploaded` : '';
    this.skippedFilesProgress = this.skippedFilesCount > 0 ? `${this.skippedFilesCount} / ${this.totalFilesCount} files skipped` : '';
  }

  /**
   * Uploads a file to the server and handles additional processing such as adding keywords and document notes.
   *
   * @param identifier - The identifier for the file upload. Can be null.
   * @param ipType - The type of the IP (Intellectual Property) being uploaded.
   * @param fileWrapper - The file wrapper containing the file and additional metadata.
   * @returns An Observable of the upload response.
   */
  private uploadFile(identifier: string | null, ipType: IP.TypeEnum, fileWrapper: FileWithProgress): Observable<UploadResponse> {
    const blob = this.fileDataCache[fileWrapper.id]?.data;

    if (ipType === IP.TypeEnum.Reference || ipType === IP.TypeEnum.CaseReference) {
      return this.apiService.uploadFileToReference(identifier, fileWrapper, blob, ipType).pipe(
        map((uploadFileResp) => {
          this.loggerService.debug('[UploaderContainerComponent] Upload Reference Response:', uploadFileResp);
          if (!uploadFileResp) {
            this.loggerService.error('[UploaderContainerComponent] Reference file upload failed or returned null');
            throw new Error('File upload failed');
          }
          return uploadFileResp;
        }),
        catchError((error) => {
          this.loggerService.error('[UploaderContainerComponent] Error during file processing:', error);
          return throwError(error);
        })
      );
    } else {
      return this.apiService.uploadFileToTrifold(identifier!, fileWrapper, blob, ipType as IP.TrifTypeEnum).pipe(
        switchMap((uploadFileResp) => {
          this.loggerService.debug('[UploaderContainerComponent] Upload File Response:', uploadFileResp);

          if (!uploadFileResp) {
            this.loggerService.error('[UploaderContainerComponent] File upload failed or returned null');
            throw new Error('File upload failed');
          }

          fileWrapper.id = uploadFileResp.fileId + '';

          // Proceed to add keywords if present
          if (fileWrapper.keywords) {
            this.loggerService.debug('[UploaderContainerComponent] Keywords detected, proceeding to add keywords.');
            return this.apiService.addKeywordToFile(identifier!, fileWrapper, ipType as IP.TrifTypeEnum).pipe(
              map((uploadKeywordResp) => {
                this.loggerService.debug('[UploaderContainerComponent] Add Keyword Response:', uploadKeywordResp);
                return { uploadFileResp, uploadKeywordResp };
              }),
              catchError((error) => {
                this.loggerService.error('[UploaderContainerComponent] Error adding keyword:', error);
                return of({ uploadFileResp, uploadKeywordResp: null });
              })
            );
          } else {
            this.loggerService.debug('[UploaderContainerComponent] No keywords found, skipping add keyword step.');
            return of({ uploadFileResp, uploadKeywordResp: null });
          }
        }),
        switchMap(({ uploadFileResp, uploadKeywordResp }) => {
          this.loggerService.debug('[UploaderContainerComponent] Proceeding to add doc notes.');
          // Proceed to add doc notes if present
          if (fileWrapper.docNotes) {
            return this.apiService.addDocNoteToFile(identifier!, fileWrapper, ipType as IP.TrifTypeEnum).pipe(
              map((uploadDocNoteResp) => {
                this.loggerService.debug('[UploaderContainerComponent] Add Doc Note Response:', uploadDocNoteResp);
                return { uploadFileResp, uploadKeywordResp, uploadDocNoteResp };
              }),
              catchError((error) => {
                this.loggerService.error('[UploaderContainerComponent] Error adding doc note:', error);
                return of({ uploadFileResp, uploadKeywordResp, uploadDocNoteResp: null });
              })
            );
          } else {
            this.loggerService.debug('[UploaderContainerComponent] No doc notes found, skipping add doc note step.');
            return of({ uploadFileResp, uploadKeywordResp, uploadDocNoteResp: null });
          }
        }),
        catchError((err) => {
          this.loggerService.error('[UploaderContainerComponent] Error during file processing:', err);
          return throwError(err); // Propagate error if needed
        })
      );
    }
  }

  /**
   * Creates an observable for paginated search results based on form values and page index.
   * Updates pagination info and returns filtered results.
   *
   * @param displayNumber - Number of items to display per page
   * @returns Observable of CommonIP array containing search results
   */
  private createSearchItemsObservable(displayNumber: number = 10): Observable<CommonIP[]> {
    this.form.get('search')!.valueChanges.subscribe(() => {
      this.resetAutocomplete();
    });
    return combineLatest([
      this.form.get('search')!.valueChanges.pipe(startWith(this.form.get('search')!.value)),
      this.form.get('ipType')!.valueChanges.pipe(startWith(this.form.get('ipType')!.value)),
      this.currentPageIndex.asObservable(),
    ]).pipe(
      takeUntil(this.$ngDestroyed),
      debounceTime(1000),
      map(() => {
        const search = this.form.get('search')?.value;
        const ipType = this.form.get('ipType')?.value;
        const pageIndex = this.currentPageIndex.value;
        return [search, ipType, pageIndex];
      }),
      switchMap(([search, ipType, pageIndex]) => {
        if (!!search && !!ipType) {
          return this.apiService.findIPItemsContaining(search, [ipType], pageIndex * displayNumber, displayNumber);
        } else {
          return of(emptyCriteria<CommonIP>(pageIndex * displayNumber, displayNumber));
        }
      }),
      tap((criteria) => this.updatePaginationInfo(criteria, displayNumber)),
      map((criteria) => criteria.results)
    );
  }

  /**
   * Updates pagination information based on search criteria.
   * Triggers change detection after update.
   *
   * @param criteria - The search criteria containing result information
   * @param displayNumber - Number of items displayed per page
   */
  private updatePaginationInfo(criteria: { totalRows: number; currentPage?: number }, displayNumber: number): void {
    this.ngZone.run(() => {
      const totalPages = Math.max(1, Math.ceil(criteria.totalRows / displayNumber));
      const currentPage = Math.floor((criteria.currentPage ?? 0) / displayNumber) + 1;

      this.paginationInfo.next({
        currentPage,
        totalPages,
      });
      this.cdr.detectChanges();
    });
  }

  /**
   * Updates the placeholder text based on the selected IP type.
   * @param ipType The selected IP type.
   */
  private updatePlaceholderText(ipType: IP.TypeEnum): void {
    switch (ipType) {
      case 'Patent':
        this.placeholderText = 'Search for patent cases';
        break;
      case 'Trademark':
        this.placeholderText = 'Search for trademarks';
        break;
      case 'Matter':
        this.placeholderText = 'Search for other matters';
        break;
      default:
        this.placeholderText = 'Search for cases';
    }
  }

  private resetAutocomplete() {
    if (this.presentationComponent?.autocomplete) {
      this.presentationComponent.autocomplete.reset();
    }
    this.currentPageIndex.next(0);
  }
}
